import { useEffect } from 'react';
import { FieldNamesMarkedBoolean, useFormContext } from 'react-hook-form';
import { Prompt } from 'react-router';
import { Location } from 'history';
import { useNavigate, useLocation } from 'react-router-dom-v5-compat';

import { useUnsavedChangesPrompt } from 'modules/ai/ui/components/UnsavedChanges/useUnsavedChangesModal';
import { useModalContext } from 'utils/contexts/modal';
import {
  UnsavedChangesModal,
  UnsavedChangesModalId,
} from 'modules/frequencyManagement/components/layout/UnsavedChangesModal';
import { UnsavedMediumModel, UnsavedMessageType } from 'modules/frequencyManagement/types';
import { ConfigByMedium } from 'modules/frequencyManagement/lib/types';
import {
  FrequencyManagementMessageType,
  SupportedMediums,
} from 'modules/frequencyManagement/lib/types';
import { useOnSubmit } from 'modules/frequencyManagement/hooks/useOnSubmit';
import {
  createPayload,
  createUnsavedFormMessageTypeModel,
  getUnsavedMediumModel,
} from 'modules/frequencyManagement/hooks/useOnSubmit.utils';
import { FrequencyManagementSchema } from 'modules/frequencyManagement/contexts/FormProvider';
import { useCoreContext } from 'modules/frequencyManagement/contexts/CoreContext';

export const getUnsavedModel = (
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<FrequencyManagementSchema>>>,
  messageMedium: SupportedMediums,
  getValues: () => FrequencyManagementSchema,
) => {
  const unsavedMediumModel = getUnsavedMediumModel(dirtyFields, messageMedium);
  const mappedUnsavedCompleteModel = createPayload(
    messageMedium,
    createUnsavedFormMessageTypeModel(dirtyFields, getValues()),
  );
  // Remove the always included message medium
  const mappedMessageTypeIds = (
    mappedUnsavedCompleteModel.slice(1) as Array<UnsavedMessageType>
  ).map((messageType: UnsavedMessageType) => messageType.messageTypeId);

  return {
    unsavedMediumModel,
    mappedMessageTypeIds,
  };
};

export const getUnsavedChangesNames = (
  unsavedMediumModel: UnsavedMediumModel,
  messageMedium: SupportedMediums,
  messageTypeIds: number[],
  frequencyManagementData: ConfigByMedium,
) => {
  const unsavedChangesNames = [] as string[];
  if (unsavedMediumModel.isUnsaved) {
    unsavedChangesNames.push(unsavedMediumModel.name);
  }

  const storedMessageTypes = frequencyManagementData[messageMedium].messageTypes;
  messageTypeIds.forEach((messageTypeId) => {
    const matchingMessageType = storedMessageTypes.find(
      (storedMessageType) => storedMessageType.id === messageTypeId,
    ) as FrequencyManagementMessageType;
    unsavedChangesNames.push(matchingMessageType.name);
  });
  return unsavedChangesNames;
};

export const UnsavedChanges = ({ messageMedium }: { messageMedium: SupportedMediums }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { frequencyManagementData } = useCoreContext();
  const {
    formState: { dirtyFields, isDirty, isValid },
    getValues,
  } = useFormContext<FrequencyManagementSchema>();
  const { showModal, hideModal, modalState } = useModalContext();
  const { changesConfirmed, confirmChanges, nextLocation, promptForUnsavedChanges } =
    useUnsavedChangesPrompt(showModal, UnsavedChangesModalId);

  const { unsavedMediumModel, mappedMessageTypeIds } = getUnsavedModel(
    dirtyFields,
    messageMedium,
    getValues,
  );

  const unsavedChangesNames = getUnsavedChangesNames(
    unsavedMediumModel,
    messageMedium,
    mappedMessageTypeIds,
    frequencyManagementData,
  );

  const { onSubmit, loading } = useOnSubmit(messageMedium);

  const onSave = () => {
    onSubmit();
    hideModal();
    if (modalState === UnsavedChangesModalId) {
      confirmChanges();
    }
  };

  const compareWithPathnames = (nextLoc: Location) => {
    if (pathname === nextLoc.pathname) {
      return true;
    } else {
      return promptForUnsavedChanges(nextLoc);
    }
  };

  useEffect(() => {
    if (changesConfirmed && nextLocation) {
      navigate(nextLocation?.pathname || '');
    }
  }, [changesConfirmed, nextLocation, navigate]);

  return (
    <>
      <Prompt when={isDirty} message={compareWithPathnames} />
      {isDirty && (
        <UnsavedChangesModal
          onClose={hideModal}
          onLeave={() => {
            confirmChanges();
            hideModal();
          }}
          onSave={onSave}
          isLoading={loading}
          isValid={isValid}
          unsavedChangesNames={unsavedChangesNames}
        />
      )}
    </>
  );
};
